import { TFunction } from 'i18next';
import { localizeDigitsForLocale } from './arabicDigits';
import { getReturnTypeNameLabel, toReturnTypeSlug } from './returnTypeLabel';

const SUBSECTION_NAME_KEYS: Record<string, string> = {
  Goods: 'returns.mapping.subsection-names.goods',
  Services: 'returns.mapping.subsection-names.services',
  'Triangular Trade': 'returns.mapping.subsection-names.triangular-trade',
  'OUT OF SCOPE': 'returns.mapping.subsection-names.out-of-scope',
  'Out of Scope': 'returns.mapping.subsection-names.out-of-scope',
  Ignore: 'returns.mapping.subsection-names.ignore',
  Other: 'returns.mapping.subsection-names.other',
  'Tax rates to be used on another country':
    'returns.mapping.subsection-names.other-country-tax-rates',
};

const SUBSECTION_NAME_KEYS_LOWER = Object.fromEntries(
  Object.entries(SUBSECTION_NAME_KEYS).map(([name, key]) => [
    name.toLowerCase(),
    key,
  ])
);

const MISSING_KEY_SENTINEL = '__mapping_subsection_missing__';

/** Translate subsection/box labels from the returns API (Goods, Services, etc.). */
export function getMappingSubsectionLabel(
  name: string | undefined,
  t: TFunction
): string {
  if (!name?.trim()) return '';

  const trimmed = name.trim();
  const exactKey =
    SUBSECTION_NAME_KEYS[trimmed] ??
    SUBSECTION_NAME_KEYS_LOWER[trimmed.toLowerCase()];
  if (exactKey) {
    const translated = t(exactKey, { defaultValue: MISSING_KEY_SENTINEL });
    if (translated && translated !== MISSING_KEY_SENTINEL) {
      return translated;
    }
  }

  const slugKey = `returns.mapping.subsection-names.${toReturnTypeSlug(trimmed)}`;
  const slugTranslated = t(slugKey, { defaultValue: MISSING_KEY_SENTINEL });
  if (slugTranslated && slugTranslated !== MISSING_KEY_SENTINEL) {
    return slugTranslated;
  }

  return trimmed;
}

/** Translate section titles shown in mapping overviews (often match return type names). */
export function getMappingSectionLabel(
  name: string | undefined,
  t: TFunction,
  language?: string
): string {
  if (!name?.trim()) return '';
  const trimmed = name.trim();
  const subsectionLabel = getMappingSubsectionLabel(trimmed, t);
  if (subsectionLabel !== trimmed) return subsectionLabel;
  return getReturnTypeNameLabel(trimmed, t, language);
}

/** Keep provider tax-rate names; localize numeric rate suffix for Arabic UI. */
export function formatMappingTaxRateLabel(
  taxCodeName: string,
  taxRate: number | string | undefined,
  reverseCharge: string | undefined,
  locale?: string | null
): string {
  const rateText = reverseCharge
    ? reverseCharge.replace(/[.,]00$/, '')
    : taxRate?.toString().replace(/[.,]00$/, '') ?? '';
  return `${taxCodeName} [${localizeDigitsForLocale(rateText, locale)}%]`;
}
